import { createGlobalStyle, css, type DefaultTheme } from 'styled-components'

// To make the effect more/less intense/visible as the mouse moves, modify parameters 1 to 3.
// To make the baseline gradient itself more/less intense/visible, modify parameters 4 and 5.
export const PROMO_TINT = {
  // 1) How intense is the maxed out opacity of the effect, relative to its intensity on hover.
  restingMaxStrength: 0.8,
  // 2) How close to a promo nav item the effect maxes out.
  fullDistancePx: 50,
  // 3) How far away from a promo nav item the effect starts.
  noneDistancePx: 200,
  proximitySteps: 50,
  restingTransitionMs: 600,
  restingEasing: 'cubic-bezier(0.33, 1, 0.68, 1)',
  hoverTransitionMs: 250,
  reducedMotionTransitionMs: 150,
  // 4) Tint left on the right side of the row, relative to the left edge.
  gradientTailStrength: 0.25,
  // 5) Point across the row (% of width) where the fade ends and the tint goes flat.
  gradientFadeEndPercent: 80,
} as const

export const PROMO_PROXIMITY_PROPERTY = '--promo-proximity'

export const PromoTintGlobalStyle = createGlobalStyle`
  /* stylelint-disable-next-line at-rule-prelude-no-invalid -- PROMO_PROXIMITY_PROPERTY interpolation causes false positive */
  @property ${PROMO_PROXIMITY_PROPERTY} {
    syntax: '<number>';
    inherits: false;
    initial-value: 0;
  }
`

const gradientTintOverlayEffect =
  (share: number) => (props: { theme: DefaultTheme }) =>
    `color-mix(in srgb, ${props.theme.colors.badge.discover.background} ${share * 100}%, transparent)`

/**
 * Shared promo row tint; rows opt in through the `data-promo` attribute.
 * The gradientTintOverlayEffect lives on a pseudo-element so proximity changes animate its `opacity`,
 * which is GPU bound and performant
 */
export const promoTintStyles = css`
  &[data-promo]:not(.active) {
    @media (width > ${(props) => props.theme.breakpoints.values.xl}px) {
      isolation: isolate;

      &::before {
        ${PROMO_PROXIMITY_PROPERTY}: inherit;
        content: '';
        position: absolute;
        inset: 0;
        z-index: -1;
        border-radius: inherit;
        pointer-events: none;
        background-image: linear-gradient(
          90deg,
          ${gradientTintOverlayEffect(1)} 0%,
          ${gradientTintOverlayEffect(PROMO_TINT.gradientTailStrength)}
            ${PROMO_TINT.gradientFadeEndPercent}%,
          ${gradientTintOverlayEffect(PROMO_TINT.gradientTailStrength)} 100%
        );
        opacity: calc(
          var(${PROMO_PROXIMITY_PROPERTY}) * ${PROMO_TINT.restingMaxStrength}
        );
        transition: opacity ${PROMO_TINT.restingTransitionMs}ms
          ${PROMO_TINT.restingEasing};
      }

      &:focus-visible::before {
        opacity: 1;
        transition-duration: ${PROMO_TINT.hoverTransitionMs}ms;
      }

      @media (hover: hover) {
        &:hover::before {
          opacity: 1;
          transition-duration: ${PROMO_TINT.hoverTransitionMs}ms;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        &::before,
        &:focus-visible::before,
        &:hover::before {
          transition-duration: ${PROMO_TINT.reducedMotionTransitionMs}ms;
          transition-timing-function: linear;
        }
      }

      @media (forced-colors: active) {
        &::before {
          display: none;
        }
      }
    }
  }
`
